import axios from "axios";
import Cookies from "js-cookie";
import { CreatedMenu } from "../features/addMenu";
import { BlogPost } from "../types/blog";
import { CategoryItem, CategoryListResponse } from "../types/category";
import { ChangeLogDataType } from "../types/change-log";
import {
    CustomFooterCreateRequest,
    CustomFooterGet,
    CustomFooterResponse,
    CustomFooterUpdateRequest,
} from "../types/footer";
import {
    CustomHeaderCreateRequest,
    CustomHeaderGet,
    CustomHeaderResponse,
    CustomHeaderUpdateRequest
} from "../types/header";
import { ThemeListResponse } from "../types/theme";
import { CustomWidgetGet, CustomWidgetItem } from "../types/widget";
import { ChangeLogItemType } from "./../types/change-log.d";
import { CustomFormItem, CustomFormResponse } from "./../types/form.d";
import { ProductDetailsType, ProductItemGet } from "./../types/product.d";
import { CustomWidgetDeleteRes, CustomWidgetPost } from "./../types/widget.d";
import { adminTokenCookieName, envs } from "./constants";

interface Pagination {
	perPage?: number;
	currentPage?: number;
	search?: string;
	status?: string;
}

export const getWidgetList = (): Promise<CustomWidgetGet> => {
	return new Promise(async (resolve, reject) => {
		const token = Cookies.get(adminTokenCookieName);

		try {
			const res = await axios.get(
				process.env.REACT_APP_BUILDER_URL + "widget?perPage=100",
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				}
			);

			resolve(res.data);
		} catch (error) {
			reject(error);
		}
	});
};

export const getWidgetItem = (id: string): Promise<CustomWidgetItem> => {
	return new Promise(async (resolve, reject) => {
		const token = Cookies.get(adminTokenCookieName);

		try {
			const res = await axios.get(
				process.env.REACT_APP_BUILDER_URL + "widget/" + id,
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				}
			);

			resolve(res.data);
		} catch (error) {
			reject(error);
		}
	});
};

/**
 * @desc update widget item
 * @param widgetItem
 * @returns response data
 * @author Azher Uddin <https://github.com/AzherUddin617>
 */
export const updateWidgetItem = (
	widgetItem: CustomWidgetItem
): Promise<CustomWidgetItem> => {
	return new Promise(async (resolve, reject) => {
		const token = Cookies.get(adminTokenCookieName);

		try {
			const res = await axios.patch(
				process.env.REACT_APP_BUILDER_URL + "widget/" + widgetItem._id,
				widgetItem,
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				}
			);

			resolve(res.data);
		} catch (error) {
			reject(error);
		}
	});
};

/**
 * @desc to delete widget item
 * @param widgetId
 * @returns response data
 * @author Azher Uddin <https://github.com/AzherUddin617>
 */
export const deleteWidgetItem = (
	widgetId: string
): Promise<CustomWidgetDeleteRes> => {
	return new Promise(async (resolve, reject) => {
		const token = Cookies.get(adminTokenCookieName);

		try {
			const res = await axios.delete(
				process.env.REACT_APP_BUILDER_URL + "widget/" + widgetId,
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				}
			);

			resolve(res.data);
		} catch (error) {
			reject(error);
		}
	});
};

/**
 * @desc to create widget item
 * @param widgetItem - widget settings
 * @returns response data
 * @author Azher Uddin <https://github.com/AzherUddin617>
 */
export const createWidgetItem = (
	widgetItem: CustomWidgetPost
): Promise<CustomWidgetItem> => {
	return new Promise(async (resolve, reject) => {
		const token = Cookies.get(adminTokenCookieName);

		try {
			const res = await axios.post(
				process.env.REACT_APP_BUILDER_URL + "widget",
				widgetItem,
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				}
			);

			resolve(res.data);
		} catch (error) {
			reject(error);
		}
	});
};

/**
 * @desc to get category items
 * @returns category items
 * @author Azher Uddin <https://github.com/AzherUddin617>
 * @date 2022-12-12 16:57:52
 */
export const getCategoryItems = (): Promise<CategoryItem[]> => {
	return new Promise(async (resolve, reject) => {
		try {
			const res = await axios.get(
				process.env.REACT_APP_BASE_URL + "category"
			);
			resolve(res.data);
		} catch (error) {
			reject(error);
		}
	});
};

export const getProductsBySkus = (
	skus: string[]
): Promise<ProductDetailsType[]> => {
	return new Promise(async (resolve, reject) => {
		try {
			const res = await axios.post(
				envs.frontendApiUrl + "product/storefront/get-by-skus",
				{ skus }
			);

			resolve(res.data?.data || []);
		} catch (error) {
			reject(error);
		}
	});
};

export const getProductByIds = (
	ids: number[],
	shopApiKey: string
): Promise<any> => {
	return new Promise(async (resolve, reject) => {
		try {
			const res = await axios.post(
				envs.frontendApiUrl?.replace("v1", "v3") +
					"product/storefront/get-by-ids",
				{ ids },
				{
					headers: {
						"x-api-key": shopApiKey,
					},
				}
			);

			resolve(res.data);
		} catch (error) {
			reject(error);
		}
	});
};

export const getProductsByLogic = (
	logics: any,
	shopApiKey: string
): Promise<any> => {
	return new Promise(async (resolve, reject) => {
		try {
			const res = await axios.post(
				envs.frontendApiUrl?.replace("v1", "v3") +
					"product/storefront/get-by-logic",
				logics,
				{
					headers: {
						"x-api-key": shopApiKey,
					},
				}
			);

			resolve(res.data);
		} catch (error) {
			reject(error);
		}
	});
};

export const getPageChangeLogs = (
	pageId: string,
	currentPage = 1,
	perPage = 10
): Promise<ChangeLogDataType> => {
	return new Promise(async (resolve, reject) => {
		const token = Cookies.get(adminTokenCookieName);

		try {
			const res = await axios.get(
				process.env.REACT_APP_BUILDER_URL +
					"page/histories/" +
					pageId +
					`?currentPage=${currentPage}&perPage=${perPage}`,
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				}
			);
			resolve(res.data);
		} catch (error) {
			reject(error);
		}
	});
};

export const getPageChangeLogById = (
	logId: string
): Promise<ChangeLogItemType> => {
	return new Promise(async (resolve, reject) => {
		const token = Cookies.get(adminTokenCookieName);

		try {
			const res = await axios.get(
				process.env.REACT_APP_BUILDER_URL +
					"page/histories/get-one/" +
					logId,
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				}
			);
			resolve(res.data);
		} catch (error) {
			reject(error);
		}
	});
};

export const setPageActive = (
	pageId: string,
	active: boolean
): Promise<any> => {
	return new Promise(async (resolve, reject) => {
		const token = Cookies.get(adminTokenCookieName);

		try {
			const res = await axios.patch(
				process.env.REACT_APP_BUILDER_URL + "page/active/" + pageId,
				{
					active: active,
				},
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				}
			);
			resolve(res.data);
		} catch (error) {
			reject(error);
		}
	});
};

interface CategoryProps extends Pagination {
	parentId?: string | number | null;
	status?: "active" | "inactive" | "";
	parent?: boolean;
	isFeatured?: boolean;
}

export const getCategoryList = ({
	perPage = 10,
	currentPage = 1,
	search,
	parentId,
	status,
	parent,
	isFeatured,
}: CategoryProps): Promise<CategoryListResponse> => {
	let query = "";
	if (parentId !== undefined) query += "&parentId=" + parentId;
	if (status !== undefined) query += "&status=" + status;
	if (parent !== undefined) query += "&parent=" + parent;
	if (isFeatured !== undefined) query += "&isFeatured=" + isFeatured;
	if (search !== undefined) query += "&search=" + search;

	return new Promise(async (resolve, reject) => {
		try {
			const res = await axios.get<CategoryListResponse>(
				`${envs.frontendApiUrl}category?perPage=${perPage}&currentPage=${currentPage}${query}`,
				{
					headers: {
						Authorization: `Bearer ${Cookies.get(
							adminTokenCookieName
						)}`,
					},
				}
			);
			resolve(res.data);
		} catch (error) {
			reject(error);
		}
	});
};

export const getProductList = ({
	perPage = 20,
	currentPage = 1,
	search,
	parentId,
	status,
	parent,
	isFeatured,
}: CategoryProps): Promise<ProductItemGet[]> => {
	let query = "";
	if (parentId !== undefined) query += "&parentId=" + parentId;
	if (status !== undefined) query += "&status=" + status;
	if (parent !== undefined) query += "&parent=" + parent;
	if (isFeatured !== undefined) query += "&isFeatured=" + isFeatured;
	if (search !== undefined) query += "&search=" + search;

	return new Promise(async (resolve, reject) => {
		try {
			const res = await axios.get(
				`${process.env.REACT_APP_BASE_URL}product?perPage=${perPage}&currentPage=${currentPage}${query}`,
				{
					headers: {
						Authorization: `Bearer ${Cookies.get(
							adminTokenCookieName
						)}`,
					},
				}
			);
			resolve(res.data?.data || []);
		} catch (error) {
			reject(error);
		}
	});
};

export const getBrandList = ({
	perPage = 10,
	currentPage = 1,
	search,
	status,
}: Pagination): Promise<CategoryListResponse> => {
	let query = "";
	if (status !== undefined) query += "&status=" + status;
	if (search !== undefined) query += "&search=" + search;

	return new Promise(async (resolve, reject) => {
		try {
			const res = await axios.get<CategoryListResponse>(
				`${envs.frontendApiUrl}brand?perPage=${perPage}&currentPage=${currentPage}${query}`,
				{
					headers: {
						Authorization: `Bearer ${Cookies.get(
							adminTokenCookieName
						)}`,
					},
				}
			);
			resolve(res.data);
		} catch (error) {
			reject(error);
		}
	});
};

// Widget Create: {{apiUrl}}/api/v1/widget
export const createWidget = (data: any) => {
	return new Promise(async (resolve, reject) => {
		try {
			const res = await axios.post(`${envs.frontendApiUrl}widget`, data, {
				headers: {
					Authorization: `Bearer ${Cookies.get(
						adminTokenCookieName
					)}`,
				},
			});
			resolve(res.data);
		} catch (error) {
			reject(error);
		}
	});
};

export const reqMakeHomePage = (
	pageId: string,
	mode: "desktop" | "mobile" | "all"
): Promise<any> => {
	return new Promise(async (resolve, reject) => {
		const token = Cookies.get(adminTokenCookieName);

		try {
			const res = await axios.patch(
				process.env.REACT_APP_BUILDER_URL +
					"page/default-home-page/" +
					pageId,
				{
					screenMode: mode,
				},
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				}
			);
			resolve(res.data);
		} catch (error) {
			reject(error);
		}
	});
};

// get all themes: {{apiUrl}}/api/v1/theme?currentPage=1&perPage=10&status=active
export const getThemeList = ({
	perPage = 10,
	currentPage = 1,
	search,
	status,
	shopType,
}: Pagination & {
	shopType?: number;
}): Promise<ThemeListResponse> => {
	let query = "";
	if (status !== undefined) query += "&status=" + status;
	if (search !== undefined) query += "&search=" + search;
	if (shopType !== undefined) query += "&shopType=" + shopType;

	return new Promise(async (resolve, reject) => {
		try {
			const res = await axios.get<ThemeListResponse>(
				`${envs.frontendApiUrl}theme?perPage=${perPage}&currentPage=${currentPage}${query}`,
				{
					headers: {
						Authorization: `Bearer ${Cookies.get(
							adminTokenCookieName
						)}`,
					},
				}
			);
			resolve(res.data);
		} catch (error) {
			reject(error);
		}
	});
};

// change shop theme: {{apiUrl}}/api/v1/shop/theme
export const changeShopTheme = (data: any): Promise<any> => {
	return new Promise(async (resolve, reject) => {
		try {
			const res = await axios.patch(
				`${envs.frontendApiUrl?.replace('/v1', '/v2')}shop/theme`,
				data,
				{
					headers: {
						Authorization: `Bearer ${Cookies.get(
							adminTokenCookieName
						)}`,
					},
				}
			);

			resolve(res.data);
		} catch (error) {
			reject(error);
		}
	});
};

// get shop types: {{apiUrl}}/api/v1/shop-type
export const getShopTypes = (): Promise<any[]> => {
	return new Promise(async (resolve, reject) => {
		try {
			const res = await axios.get(`${envs.frontendApiUrl}shop-type`, {
				headers: {
					Authorization: `Bearer ${Cookies.get(
						adminTokenCookieName
					)}`,
				},
			});

			resolve(res.data);
		} catch (error) {
			reject(error);
		}
	});
};

export const getMenuItems = (): Promise<CreatedMenu[]> => {
	return new Promise(async (resolve, reject) => {
		try {
			const res = await axios.get(`${envs.frontendApiUrl}menu`, {
				headers: {
					Authorization: `Bearer ${Cookies.get(
						adminTokenCookieName
					)}`,
				},
			});

			resolve(res.data);
		} catch (error) {
			reject(error);
		}
	});
};

export const getMenuItem = (id: string): Promise<CreatedMenu> => {
	return new Promise(async (resolve, reject) => {
		try {
			const res = await axios.get(`${envs.frontendApiUrl}menu/${id}`, {
				headers: {
					Authorization: `Bearer ${Cookies.get(
						adminTokenCookieName
					)}`,
				},
			});
			resolve(res.data);
		} catch (error) {
			reject(error);
		}
	});
};

// ** custom forms ** //
// get all: /api/v1/form-builder?perPage=10&currentPage=1
export const getAllCustomForms = ({
	perPage = 10,
	currentPage = 1,
}: {
	perPage?: number;
	currentPage?: number;
}): Promise<CustomFormResponse> => {
	return new Promise(async (resolve, reject) => {
		try {
			const res = await axios.get(
				`${envs.frontendApiUrl}form-builder?perPage=${perPage}&currentPage=${currentPage}`,
				{
					headers: {
						Authorization: `Bearer ${Cookies.get(
							adminTokenCookieName
						)}`,
					},
				}
			);

			resolve(res.data);
		} catch (error) {
			reject(error);
		}
	});
};

// get one: /api/v1/form-builder/:id
export const getCustomForm = (id: string): Promise<CustomFormItem> => {
	return new Promise(async (resolve, reject) => {
		try {
			const res = await axios.get(
				`${envs.frontendApiUrl}form-builder/${id}`,
				{
					headers: {
						Authorization: `Bearer ${Cookies.get(
							adminTokenCookieName
						)}`,
					},
				}
			);

			resolve(res.data);
		} catch (error) {
			reject(error);
		}
	});
};

// ** blog
// get by id: /api/v1/blog-post/:id
export const getBlogPost = (id: string): Promise<BlogPost> => {
	return new Promise(async (resolve, reject) => {
		try {
			const res = await axios.get(
				`${envs.frontendApiUrl}blog-post/${id}`,
				{
					headers: {
						Authorization: `Bearer ${Cookies.get(
							adminTokenCookieName
						)}`,
					},
				}
			);
			resolve(res.data);
		} catch (error) {
			reject(error);
		}
	});
};

// PATCH: /api/v1/blog-post/:id
export const updateBlogPost = (id: number, data: any): Promise<BlogPost> => {
	return new Promise(async (resolve, reject) => {
		try {
			const res = await axios.patch(
				`${envs.frontendApiUrl}blog-post/${id}`,
				data,
				{
					headers: {
						Authorization: `Bearer ${Cookies.get(
							adminTokenCookieName
						)}`,
					},
				}
			);
			resolve(res.data);
		} catch (error) {
			reject(error);
		}
	});
};

// ** header builder
// create header: /api/v1/header
export const createCustomHeader = (
	data: CustomHeaderCreateRequest
): Promise<CustomHeaderGet> => {
	return new Promise(async (resolve, reject) => {
		try {
			const res = await axios.post(`${envs.frontendApiUrl}header`, data, {
				headers: {
					Authorization: `Bearer ${Cookies.get(
						adminTokenCookieName
					)}`,
				},
			});
			resolve(res.data);
		} catch (error) {
			reject(error);
		}
	});
};

// update header: /api/v1/header/:id
export const updateCustomHeader = (
	id: string,
	data: CustomHeaderUpdateRequest
): Promise<CustomHeaderGet> => {
	return new Promise(async (resolve, reject) => {
		try {
			const res = await axios.patch(
				`${envs.frontendApiUrl}header/${id}`,
				data,
				{
					headers: {
						Authorization: `Bearer ${Cookies.get(
							adminTokenCookieName
						)}`,
					},
				}
			);
			resolve(res.data);
		} catch (error) {
			reject(error);
		}
	});
};

// get by id: /api/v1/header/:id
export const getCustomHeader = (id: string): Promise<CustomHeaderGet> => {
	return new Promise(async (resolve, reject) => {
		try {
			const res = await axios.get(`${envs.frontendApiUrl}header/${id}`, {
				headers: {
					Authorization: `Bearer ${Cookies.get(
						adminTokenCookieName
					)}`,
				},
			});
			resolve(res.data);
		} catch (error) {
			reject(error);
		}
	});
};

// get all: /api/v1/header?perPage=10&currentPage=1&search=undefined&status=active
export const getAllCustomHeaders = ({
	page = 1,
	perPage = 10,
	search,
	status,
}: {
	page?: number;
	perPage?: number;
	search?: string;
	status?: string;
}): Promise<CustomHeaderResponse> => {
	return new Promise(async (resolve, reject) => {
		let extraQuery = "";
		if (search) extraQuery += `&search=${search}`;
		if (status) extraQuery += `&status=${status}`;

		try {
			const res = await axios.get(
				`${envs.frontendApiUrl}header?perPage=${perPage}&currentPage=${page}${extraQuery}`,
				{
					headers: {
						Authorization: `Bearer ${Cookies.get(
							adminTokenCookieName
						)}`,
					},
				}
			);
			resolve(res.data);
		} catch (error) {
			reject(error);
		}
	});
};

// delete: /api/v1/header/:id
export const deleteCustomHeader = (id: string): Promise<any> => {
	return new Promise(async (resolve, reject) => {
		try {
			const res = await axios.delete(
				`${envs.frontendApiUrl}header/${id}`,
				{
					headers: {
						Authorization: `Bearer ${Cookies.get(
							adminTokenCookieName
						)}`,
					},
				}
			);
			resolve(res.data);
		} catch (error) {
			reject(error);
		}
	});
};

// create header global: /api/v1/global-header
export const createGlobalHeader = (
	data: CustomHeaderCreateRequest
): Promise<CustomHeaderGet> => {
	return new Promise(async (resolve, reject) => {
		try {
			const res = await axios.post(
				`${envs.frontendApiUrl}global-header`,
				data,
				{
					headers: {
						Authorization: `Bearer ${Cookies.get(
							adminTokenCookieName
						)}`,
					},
				}
			);
			resolve(res.data);
		} catch (error) {
			reject(error);
		}
	});
};

// update: /api/v1/global-header/:id
export const updateGlobalHeader = (
	id: string,
	data: CustomHeaderUpdateRequest
): Promise<CustomHeaderGet> => {
	return new Promise(async (resolve, reject) => {
		try {
			const res = await axios.patch(
				`${envs.frontendApiUrl}global-header/${id}`,
				data,
				{
					headers: {
						Authorization: `Bearer ${Cookies.get(
							adminTokenCookieName
						)}`,
					},
				}
			);
			resolve(res.data);
		} catch (error) {
			reject(error);
		}
	});
};

// get by id: /api/v1/global-header/:id
export const getGlobalHeader = (id: string): Promise<CustomHeaderGet> => {
	return new Promise(async (resolve, reject) => {
		try {
			const res = await axios.get(
				`${envs.frontendApiUrl}global-header/${id}`,
				{
					headers: {
						Authorization: `Bearer ${Cookies.get(
							adminTokenCookieName
						)}`,
					},
				}
			);
			resolve(res.data);
		} catch (error) {
			reject(error);
		}
	});
};

// get all: /api/v1/global-header?perPage=10&currentPage=1&search=undefined&status=active
export const getAllGlobalHeaders = ({
	page = 1,
	perPage = 10,
	search,
	status,
}: {
	page?: number;
	perPage?: number;
	search?: string;
	status?: string;
}): Promise<CustomHeaderResponse> => {
	return new Promise(async (resolve, reject) => {
		try {
            let extraQuery = "";
			if (search) extraQuery += `&search=${search}`;
			if (status) extraQuery += `&status=${status}`;

			const res = await axios.get(
				`${envs.frontendApiUrl}global-header?perPage=${perPage}&currentPage=${page}${extraQuery}`,
				{
					headers: {
						Authorization: `Bearer ${Cookies.get(
							adminTokenCookieName
						)}`,
					},
				}
			);
			resolve(res.data);
		} catch (error) {
			reject(error);
		}
	});
};

// delete: /api/v1/global-header/:id
export const deleteGlobalHeader = (id: number): Promise<any> => {
	return new Promise(async (resolve, reject) => {
		try {
			const res = await axios.delete(
				`${envs.frontendApiUrl}global-header/${id}`,
				{
					headers: {
						Authorization: `Bearer ${Cookies.get(
							adminTokenCookieName
						)}`,
					},
				}
			);
			resolve(res.data);
		} catch (error) {
			reject(error);
		}
	});
};

// ** custom footer
// create: /api/v1/footer
export const createCustomFooter = (
	data: CustomFooterCreateRequest
): Promise<CustomFooterGet> => {
	return new Promise(async (resolve, reject) => {
		try {
			const res = await axios.post(`${envs.frontendApiUrl}footer`, data, {
				headers: {
					Authorization: `Bearer ${Cookies.get(
						adminTokenCookieName
					)}`,
				},
			});
			resolve(res.data);
		} catch (error) {
			reject(error);
		}
	});
};

// update: /api/v1/footer/:id
export const updateCustomFooter = (
	id: string,
	data: CustomFooterUpdateRequest
): Promise<CustomFooterGet> => {
	return new Promise(async (resolve, reject) => {
		try {
			const res = await axios.patch(
				`${envs.frontendApiUrl}footer/${id}`,
				data,
				{
					headers: {
						Authorization: `Bearer ${Cookies.get(
							adminTokenCookieName
						)}`,
					},
				}
			);
			resolve(res.data);
		} catch (error) {
			reject(error);
		}
	});
};

// get by id: /api/v1/footer/:id
export const getCustomFooter = (id: string): Promise<CustomFooterGet> => {
	return new Promise(async (resolve, reject) => {
		try {
			const res = await axios.get(`${envs.frontendApiUrl}footer/${id}`, {
				headers: {
					Authorization: `Bearer ${Cookies.get(
						adminTokenCookieName
					)}`,
				},
			});
			resolve(res.data);
		} catch (error) {
			reject(error);
		}
	});
};

// get all: /api/v1/footer?perPage=10&currentPage=1&search=undefined&status=active
export const getAllCustomFooters = ({
	page = 1,
	perPage = 10,
	search,
	status,
}: {
	page?: number;
	perPage?: number;
	search?: string;
	status?: string;
}): Promise<CustomFooterResponse> => {
	return new Promise(async (resolve, reject) => {
        let extraQuery = "";
		if (search) extraQuery += `&search=${search}`;
		if (status) extraQuery += `&status=${status}`;

		try {
			const res = await axios.get(
				`${envs.frontendApiUrl}footer?perPage=${perPage}&currentPage=${page}${extraQuery}`,
				{
					headers: {
						Authorization: `Bearer ${Cookies.get(
							adminTokenCookieName
						)}`,
					},
				}
			);
			resolve(res.data);
		} catch (error) {
			reject(error);
		}
	});
};

// delete: /api/v1/footer/:id
export const deleteCustomFooter = (id: string): Promise<any> => {
	return new Promise(async (resolve, reject) => {
		try {
			const res = await axios.delete(
				`${envs.frontendApiUrl}footer/${id}`,
				{
					headers: {
						Authorization: `Bearer ${Cookies.get(
							adminTokenCookieName
						)}`,
					},
				}
			);
			resolve(res.data);
		} catch (error) {
			reject(error);
		}
	});
};

// create global footer: /api/v1/global-footer
export const createGlobalFooter = (
	data: CustomFooterCreateRequest
): Promise<CustomFooterGet> => {
	return new Promise(async (resolve, reject) => {
		try {
			const res = await axios.post(
				`${envs.frontendApiUrl}global-footer`,
				data,
				{
					headers: {
						Authorization: `Bearer ${Cookies.get(
							adminTokenCookieName
						)}`,
					},
				}
			);
			resolve(res.data);
		} catch (error) {
			reject(error);
		}
	});
};

// update: /api/v1/global-footer/:id
export const updateGlobalFooter = (
	id: number,
	data: CustomFooterUpdateRequest
): Promise<CustomFooterGet> => {
	return new Promise(async (resolve, reject) => {
		try {
			const res = await axios.patch(
				`${envs.frontendApiUrl}global-footer/${id}`,
				data,
				{
					headers: {
						Authorization: `Bearer ${Cookies.get(
							adminTokenCookieName
						)}`,
					},
				}
			);
			resolve(res.data);
		} catch (error) {
			reject(error);
		}
	});
};

// get by id: /api/v1/global-footer/:id
export const getGlobalFooter = (id: number): Promise<CustomFooterGet> => {
	return new Promise(async (resolve, reject) => {
		try {
			const res = await axios.get(
				`${envs.frontendApiUrl}global-footer/${id}`,
				{
					headers: {
						Authorization: `Bearer ${Cookies.get(
							adminTokenCookieName
						)}`,
					},
				}
			);
			resolve(res.data);
		} catch (error) {
			reject(error);
		}
	});
};

// get all: /api/v1/global-footer/:id
export const getAllGLobalFooters = ({
	page = 1,
	perPage = 10,
	search,
	status,
}: {
	page?: number;
	perPage?: number;
	search?: string;
	status?: string;
}): Promise<CustomFooterResponse> => {
	return new Promise(async (resolve, reject) => {
		try {
            let extraQuery = "";
            if (search) extraQuery += `&search=${search}`;
            if (status) extraQuery += `&status=${status}`;

			const res = await axios.get(
				`${envs.frontendApiUrl}global-footer?perPage=${perPage}&currentPage=${page}${extraQuery}`,
				{
					headers: {
						Authorization: `Bearer ${Cookies.get(
							adminTokenCookieName
						)}`,
					},
				}
			);
			resolve(res.data);
		} catch (error) {
			reject(error);
		}
	});
};

// delete: /api/v1/global-footer/:id
export const deleteGlobalFooter = (id: number): Promise<any> => {
	return new Promise(async (resolve, reject) => {
		try {
			const res = await axios.delete(
				`${envs.frontendApiUrl}global-footer/${id}`,
				{
					headers: {
						Authorization: `Bearer ${Cookies.get(
							adminTokenCookieName
						)}`,
					},
				}
			);
			resolve(res.data);
		} catch (error) {
			reject(error);
		}
	});
};


// ** make header or footer default
// /api/v1/header/default-home-header/:id
export const makeHeaderDefault = (
	id: string,
    screenMode: string
): Promise<CustomHeaderGet> => {
	return new Promise(async (resolve, reject) => {
		try {
			const res = await axios.patch(
				`${envs.frontendApiUrl}header/default-home-header/${id}`,
				{
                    screenMode
                },
				{
					headers: {
						Authorization: `Bearer ${Cookies.get(
							adminTokenCookieName
						)}`,
					},
				}
			);
			resolve(res.data);
		} catch (error) {
			reject(error);
		}
	});
};

// ** unselect default header
// /api/v1/header/unselect-default-header/:id
export const unselectDefaultHeader = (
	id: string
): Promise<CustomHeaderGet> => {
	return new Promise(async (resolve, reject) => {
		try {
			const res = await axios.patch(
				`${envs.frontendApiUrl}header/unselect-default-header/${id}`,
				{},
				{
					headers: {
						Authorization: `Bearer ${Cookies.get(
							adminTokenCookieName
						)}`,
					},
				}
			);
			resolve(res.data);
		} catch (error) {
			reject(error);
		}
	});
};

// /api/v1/footer/default-home-footer/:id
export const makeFooterDefault = (
	id: string,
    screenMode: string
): Promise<CustomFooterGet> => {
	return new Promise(async (resolve, reject) => {
		try {
			const res = await axios.patch(
				`${envs.frontendApiUrl}footer/default-home-footer/${id}`,
				{
                    screenMode
                },
				{
					headers: {
						Authorization: `Bearer ${Cookies.get(
							adminTokenCookieName
						)}`,
					},
				}
			);
			resolve(res.data);
		} catch (error) {
			reject(error);
		}
	});
};